import type {
  ComparacaoProfissao,
  SalarioResultsType,
  SimulacaoInvestimento,
} from "../types";

import {
  BRASIL_MEDIA,
  RANKING_ESTADOS,
  SALARIOS_MEDIOS_ESTADO,
} from "../data/dadosPNAD";

import { obterProfissoesEstado } from "../data/dadosRAIS";

import {
  CARGOS_PUBLICOS,
  DISTRIBUICAO_PERCENTIL_NACIONAL,
  ESTADOS_BRASIL,
  ICONES_PROFISSOES,
  MAGISTRADOS_POR_ESTADO,
  RENTABILIDADE_INVESTIMENTO,
  obterDistribuicaoEstado,
} from "../data/constants";

// Função auxiliar para conversão de string monetária para número
const parseNumericValue = (value: string): number => {
  if (!value || typeof value !== "string") return 0;
  return (
    Number.parseFloat(value.replace(/[^0-9,-]+/g, "").replace(",", ".")) || 0
  );
};

// Função auxiliar para arredondar valores monetários apenas na apresentação final
const arredondarMoeda = (valor: number): number =>
  Math.round(valor * 100) / 100;

/**
 * Obtém o salário mediano do magistrado estadual para o estado
 */
const obterSalarioMagistrado = (estado: string): number => {
  return (
    MAGISTRADOS_POR_ESTADO[estado as keyof typeof MAGISTRADOS_POR_ESTADO] ||
    CARGOS_PUBLICOS.MAGISTRADO_ESTADUAL
  );
};

/**
 * Função para calcular percentil tratando platôs e curvas não-lineares
 * @param salarioUsuario - Salário do usuário
 * @param distribuicao - Objeto com percentis e valores
 * @returns Percentil calculado
 */
const calcularPercentilPorInterpolacao = (
  salarioUsuario: number,
  distribuicao: Record<string, number>,
): number => {
  // Converte para array e remove duplicatas de percentil mantendo o maior valor
  const percentisUnicos = new Map<number, number>();

  Object.entries(distribuicao).forEach(([perc, valor]) => {
    const percentil = Number(perc);
    const valorAtual = percentisUnicos.get(percentil);
    if (!valorAtual || valor > valorAtual) {
      percentisUnicos.set(percentil, valor);
    }
  });

  const percentis = Array.from(percentisUnicos.entries()).sort(
    (a, b) => a[1] - b[1],
  ); // Ordena por VALOR, não percentil

  // Se o salário é muito baixo
  if (salarioUsuario <= percentis[0][1]) {
    return Math.max(1, percentis[0][0] - 3);
  }

  // Se o salário é muito alto
  if (salarioUsuario >= percentis[percentis.length - 1][1]) {
    return Math.min(99, percentis[percentis.length - 1][0] + 1);
  }

  // Busca o intervalo correto por valor
  for (let i = 0; i < percentis.length - 1; i++) {
    const [percentilAtual, salarioAtual] = percentis[i];
    const [percentilProximo, salarioProximo] = percentis[i + 1];

    if (salarioUsuario >= salarioAtual && salarioUsuario <= salarioProximo) {
      // Para valores muito próximos, retorna o percentil exato
      if (Math.abs(salarioUsuario - salarioAtual) < 50) return percentilAtual;
      if (Math.abs(salarioUsuario - salarioProximo) < 50)
        return percentilProximo;

      // Interpolação suavizada (não-linear para refletir curva real)
      const proporcaoSalario =
        (salarioUsuario - salarioAtual) / (salarioProximo - salarioAtual);

      // Aplica curva logarítmica para valores baixos, linear para valores altos
      let proporcaoAjustada;
      if (salarioUsuario < 3000) {
        // Curva logarítmica para concentração na base
        proporcaoAjustada = Math.log(1 + proporcaoSalario * 2) / Math.log(3);
      } else {
        // Interpolação linear para valores altos
        proporcaoAjustada = proporcaoSalario;
      }

      const percentilInterpolado =
        percentilAtual +
        (percentilProximo - percentilAtual) * proporcaoAjustada;
      return Math.min(99, Math.max(1, Math.round(percentilInterpolado)));
    }
  }

  return 50;
};

/**
 * Calcula o percentil nacional baseado na distribuição PNAD calibrada
 */
export const calcularPercentilNacional = (salarioUsuario: number): number => {
  return calcularPercentilPorInterpolacao(
    salarioUsuario,
    DISTRIBUICAO_PERCENTIL_NACIONAL,
  );
};

/**
 * Calcula o percentil estadual baseado na distribuição específica do estado
 */
export const calcularPercentilEstadual = (
  salarioUsuario: number,
  estado: string,
): number => {
  const distribuicaoEstado = obterDistribuicaoEstado(estado);
  return calcularPercentilPorInterpolacao(salarioUsuario, distribuicaoEstado);
};

/**
 * Calcula a posição do estado no ranking nacional
 */
export const calcularRankingEstado = (
  estado: string,
): { posicao: number; total: number; posicaoTexto: string } => {
  const rankingInfo = RANKING_ESTADOS.find((item) => item.uf === estado);

  if (!rankingInfo) {
    return { posicao: 14, total: 27, posicaoTexto: "14º lugar" }; // Fallback para posição média
  }

  return {
    posicao: rankingInfo.posicao,
    total: 27,
    posicaoTexto: `${rankingInfo.posicao}º lugar`,
  };
};

/**
 * Compara salário do usuário com profissões do estado
 */
export const compararComProfissoes = (
  salarioUsuario: number,
  estado: string,
): Array<ComparacaoProfissao> => {
  const dadosProfissoes = obterProfissoesEstado(estado);

  return [
    {
      nome: "Médico",
      icone: ICONES_PROFISSOES.medico,
      salarioMedio: dadosProfissoes.medico,
      percentualUsuario: Math.round(
        (salarioUsuario / dadosProfissoes.medico) * 100,
      ),
    },
    {
      nome: "Enfermeiro",
      icone: ICONES_PROFISSOES.enfermeiro,
      salarioMedio: dadosProfissoes.enfermeiro,
      percentualUsuario: Math.round(
        (salarioUsuario / dadosProfissoes.enfermeiro) * 100,
      ),
    },
    {
      nome: "Professor",
      icone: ICONES_PROFISSOES.professor,
      salarioMedio: dadosProfissoes.professor,
      percentualUsuario: Math.round(
        (salarioUsuario / dadosProfissoes.professor) * 100,
      ),
    },
  ];
};

/**
 * Simula investimento de 25% do salário com fórmula padrão de juros compostos
 */
export const simularInvestimento25Porcento = (
  salario: number,
): SimulacaoInvestimento => {
  const aporteMensal = salario * 0.25;
  const rentabilidadeAnual = RENTABILIDADE_INVESTIMENTO;
  const rentabilidadeMensal = Math.pow(1 + rentabilidadeAnual, 1 / 12) - 1;

  const calcularMontante = (anos: number): number => {
    const meses = anos * 12;

    // Fórmula padrão: M = PMT × [((1 + i)^n - 1) / i]
    // Onde PMT = pagamento mensal, i = taxa mensal, n = número de meses
    const montante =
      aporteMensal *
      ((Math.pow(1 + rentabilidadeMensal, meses) - 1) / rentabilidadeMensal);

    return Math.round(montante);
  };

  return {
    aporteMenusal: arredondarMoeda(aporteMensal),
    em10Anos: calcularMontante(10),
    em25Anos: calcularMontante(25),
    em30Anos: calcularMontante(30),
  };
};

/**
 * Obtém o nome completo do estado
 */
export const obterNomeEstado = (sigla: string): string => {
  const estado = ESTADOS_BRASIL.find((e) => e.value === sigla);
  return estado ? estado.label : sigla;
};

/**
 * Validação dos parâmetros de entrada
 */
export const validarParametrosSalario = (
  salario: string,
  estado: string,
): void => {
  const salarioNum = parseNumericValue(salario);

  if (salarioNum <= 0) {
    throw new Error("Salário deve ser maior que zero");
  }

  if (salarioNum > 100000) {
    throw new Error("Salário muito alto. Verifique o valor informado.");
  }

  if (!estado) {
    throw new Error("Estado deve ser selecionado");
  }
};

/**
 * Função principal que calcula todos os resultados da calculadora
 */
export const calcularSalarioRealidadeBrasileira = (
  salario: string,
  estado: string,
): SalarioResultsType => {
  // Validação dos parâmetros
  validarParametrosSalario(salario, estado);

  const salarioNum = parseNumericValue(salario);

  // Cálculos principais usando as novas funções calibradas
  const percentilNacional = calcularPercentilNacional(salarioNum);
  const percentilEstadual = calcularPercentilEstadual(salarioNum, estado);
  const ranking = calcularRankingEstado(estado);
  const profissoes = compararComProfissoes(salarioNum, estado);
  const simulacao = simularInvestimento25Porcento(salarioNum);

  // Comparações com cargos públicos
  const comparacaoDeputado = Math.round(
    (salarioNum / CARGOS_PUBLICOS.DEPUTADO_FEDERAL) * 100,
  );

  // Obtém salário do magistrado específico do estado
  const salarioMagistradoEstado = obterSalarioMagistrado(estado);
  const comparacaoMagistrado = Math.round(
    (salarioNum / salarioMagistradoEstado) * 100,
  );

  // Obter salários médios
  const salarioMedioEstado =
    SALARIOS_MEDIOS_ESTADO[estado as keyof typeof SALARIOS_MEDIOS_ESTADO];
  const nomeEstado = obterNomeEstado(estado);

  return {
    percentilNacional,
    percentilEstadual,
    rankingEstado: ranking.posicao,
    posicaoRanking: ranking.posicaoTexto,
    comparacaoDeputado,
    comparacaoMagistrado,
    profissoes,
    simulacaoInvestimento: simulacao,
    salarioMedioEstado,
    salarioMedioBrasil: BRASIL_MEDIA,
    nomeEstado,
  };
};
